import React, { useState } from 'react';
import { 
  Box,
    Modal, 
  Paper,
  Typography,
    TextField, 
    Button, 
  Link,
    Alert, 
    IconButton, 
  InputAdornment,
    useTheme,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { authService } from '../services/authService';

interface SignupModalProps {
    open: boolean;
    onClose: () => void;
  onSwitchToLogin: () => void;
  redirectPath?: string;
}

const SignupModal: React.FC<SignupModalProps> = ({ 
  open, 
  onClose, 
  onSwitchToLogin,
  redirectPath = '/'
}) => {
  const theme = useTheme();
  const location = useLocation();
    const { register } = useAuth();
  const { showSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Get redirect path from location state or props
  const getRedirectPath = () => {
    if (redirectPath && redirectPath !== '/') return redirectPath;
    if ((location.state as any)?.from?.pathname) return (location.state as any).from.pathname;
    return '/';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
      return false;
    }

    if (!acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

        setLoading(true);
    setError(null);

    try {
      const from = getRedirectPath();
      await register(formData.email, formData.password, formData.firstName, formData.lastName, from);
      // Clear form data before closing
      setFormData({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
      setError(null);
      setShowPassword(false);
      setShowConfirmPassword(false);
      setAcceptedTerms(false);
      onClose();
      showSnackbar('Registration successful! Welcome to MyEventShop!', 'success');
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(
        error.response?.data?.message || 
        error.message || 
        'Failed to register. Please try again.'
      );
        } finally {
            setLoading(false);
        }
    };

  const handleClose = () => {
    setFormData({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
    setError(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setAcceptedTerms(false);
    onClose();
  };

    const handleSwitchToLogin = () => {
    onSwitchToLogin();
  };

  const handleGoogleSignUp = () => {
    authService.initiateGoogleAuth();
    };

    return (
        <Modal 
            open={open} 
      onClose={handleClose}
            aria-labelledby="signup-modal-title"
      aria-describedby="signup-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        p: 0,
      }}
    >
      <Paper
        elevation={24}
                sx={{ 
                    width: '100%',
          maxWidth: { xs: '100%', sm: '100%', md: '100%', lg: '100%', xl: '100%' },
          height: { xs: 'auto', sm: '75vh', md: '75vh', lg: '75vh', xl: '75vh' }, // Auto height on mobile, fixed 75vh on larger screens
          maxHeight: { xs: '90vh', sm: 'none', md: 'none', lg: 'none', xl: 'none' }, // Max height only on mobile
          overflow: { xs: 'visible', sm: 'auto', md: 'auto', lg: 'auto', xl: 'auto' }, // No scroll on mobile, auto scroll on larger screens
          borderRadius: 0,
          position: 'relative',
          bgcolor: '#5F3C6F', // Purple background matching header/footer
          boxShadow: theme.shadows[24],
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease-in-out',
        }}
      >

                {/* Close Button */}
                <IconButton
                    onClick={handleClose}
                    sx={{ 
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: 'white',
                      width: 32,
                      height: 32,
                      borderRadius: 0,
                      zIndex: 1,
                      '&:hover': { 
                        color: '#FF9DE7',
                        backgroundColor: 'transparent',
                      }
                    }}
                    >
                        <CloseIcon />
                    </IconButton>
                    

        {/* Form */}
                <Box 
          component="form" 
          onSubmit={handleSubmit} 
                    sx={{ 
            p: { xs: 2, sm: 2 }, 
            pt: { xs: 3, sm: 3.5 },
            maxWidth: { xs: '90%', sm: '100%', md: 500, lg: 500, xl: 500 },
            mx: 'auto'
                    }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: { xs: 1.5, sm: 2 } }}>
              {error}
            </Alert>
          )}

          {/* Email Field - Moved to top */}
          <TextField
            fullWidth
            placeholder="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
                                sx={{
              mb: { xs: 1, sm: 1.5 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                height: '56px', // Match button height
                '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                '&:hover fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                    },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                },
              },
            }}
          />

          {/* First Name Field */}
          <TextField
            fullWidth
            placeholder="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
                                required
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
                                sx={{
              mb: { xs: 1, sm: 1.5 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                height: '56px', // Match button height
                '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                '&:hover fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                    },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                },
              },
            }}
          />

          {/* Last Name Field */}
          <TextField
            fullWidth
            placeholder="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
                                required
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
                                sx={{
              mb: { xs: 1, sm: 1.5 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                height: '56px', // Match button height
                '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                '&:hover fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                    },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                },
              },
            }}
          />

          {/* Password Field */}
          <TextField
            fullWidth
            placeholder="Password"
            name="password"
                                type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
                                required
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                    size="small"
                                        >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
              ),
            }}
            sx={{ 
              mb: { xs: 1, sm: 1.5 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                height: '56px', // Match button height
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                },
              },
            }}
          />

          {/* Confirm Password Field */}
          <TextField
            fullWidth
            placeholder="Confirm Password"
            name="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange}
                                required
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                    size="small"
                                        >
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
              ),
            }}
            sx={{ 
              mb: { xs: 1.5, sm: 2 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                height: '56px', // Match button height
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                },
              },
            }}
          />

          {/* Terms Checkbox */}
          <FormControlLabel
            control={
                            <Checkbox 
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                sx={{
                  color: '#FFD700', // Yellow color for unchecked state
                                    '&.Mui-checked': {
                    color: '#FFD700', // Yellow color for checked state
                    backgroundColor: 'white', // White background when checked
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.1)', // Light yellow hover
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 215, 0, 0.1)', // Light yellow hover
                  },
                }}
              />
            }
            label={
                          <Typography
              variant="body2"
                                    sx={{ 
                                        color: 'white', 
                whiteSpace: 'nowrap',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
                I agree to the{' '}
                <Link
                  component={RouterLink}
                  to="/terms-of-service"
                                    sx={{ 
                    color: '#FFD700', // Golden yellow
                    textDecoration: 'none', 
                    '&:hover': { 
                                        textDecoration: 'underline',
                      color: '#FFC700' // Darker yellow on hover
                    } 
                  }}
                >
                  Terms
                </Link>
                {' '}&{' '}
                <Link
                  component={RouterLink}
                  to="/privacy-policy"
                  sx={{ 
                    color: '#FFD700', // Golden yellow
                    textDecoration: 'none', 
                    '&:hover': { 
                      textDecoration: 'underline',
                      color: '#FFC700' // Darker yellow on hover
                    } 
                                    }}
                                >
                                    Privacy Policy
                </Link>
                            </Typography>
            }
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          />
                        
                    {/* Submit Button */}
                        <Button
                            type="submit"
            fullWidth
                            variant="contained"
            size="large"
            disabled={loading}
                            sx={{ 
                                py: 1.2, 
              mb: { xs: 1, sm: 1.5 },
              borderRadius: 2,
              fontSize: '1rem',
                                fontWeight: 'medium',
                                textTransform: 'none',
              backgroundColor: '#FFD700',
              color: '#000',
                                '&:hover': {
                backgroundColor: '#FFC700',
              },
              '&:disabled': {
                backgroundColor: '#E0E0E0',
                color: '#9E9E9E',
              },
            }}
          >
            {loading ? 'Signing Up...' : 'Sign Up'}
                        </Button>

          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 1.5 } }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'white' }} />
            <Typography variant="body2" color="white" sx={{ px: 2 }}>
                            OR
                        </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'white' }} />
                    </Box>

          {/* Google Sign Up Button */}
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleGoogleSignUp}
                        sx={{ 
              py: 1.2,
                            mb: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              fontSize: '1rem',
              fontWeight: 'medium',
              textTransform: 'none',
              borderColor: '#dadce0',
              color: '#3c4043',
              backgroundColor: 'white',
              '&:hover': {
                borderColor: '#dadce0',
                backgroundColor: '#f8f9fa',
              },
            }}
          >
            <Box
              component="img"
              src="https://developers.google.com/identity/images/g-logo.png"
              alt="Google"
                            sx={{ 
                width: 20,
                height: 20,
                mr: 1,
              }}
            />
            Continue with Google
                        </Button>
                        
          {/* Switch to Login */}
          <Box sx={{ textAlign: 'center', pb: { xs: 1.5, sm: 2 } }}>
            <Typography variant="body2" color="white" component="span">
              Already have an account?{' '}
            </Typography>
            <Link
              component="button"
                        variant="body2" 
              onClick={handleSwitchToLogin}
                        sx={{ 
                color: '#FFD700', // Golden yellow
                textDecoration: 'none',
                            '&:hover': {
                  textDecoration: 'underline',
                  color: '#FFC700' // Darker yellow on hover
                },
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 'inherit',
              }}
            >
              Sign In
            </Link>
                </Box>
            </Box>
      </Paper>
        </Modal>
    );
};

export default SignupModal;
